import { RouteName } from "@/apps/merchant3/router/RouteName";
import { RouteName as PersonRegistrationRouteName } from "@/apps/person-registration/router/RouteName";
import { RouteRecordName, RouteLocationRaw } from "vue-router";
import { MerchantCountry } from "@/types/merchant";
import { Store } from "vuex";
import { store } from "@/shared-store";
import { IVuexState } from "@/apps/merchant3/shared-models/IVuex";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { CartActiveFeatures } from "@/apps/merchant3/types/CartActiveFeatures";
import { computed } from "vue";
import { isEcommerceFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import { getCountryCode } from "@/apps/merchant3/helpers/country";

const vuexStore = store as unknown as Store<IVuexState>;

const customerContactInfoPreviousStep = () => {
  const canUseCreditCheckPermissionsPage =
    vuexStore.state.merchant?.merchantFlags?.includes(
      MerchantFlags.USE_CREDIT_CHECK_PERMISSION_PAGE
    ) || isEcommerceFlow.value;
  const isConfigurableCart = vuexStore.getters["carts/hasActiveFeature"](
    CartActiveFeatures.CONFIGURABLE_M3
  );
  if (isConfigurableCart && canUseCreditCheckPermissionsPage) {
    return { name: RouteName.M3ConfigurableCreditCheck, params: { countryCode: getCountryCode() } };
  }
  if (canUseCreditCheckPermissionsPage) {
    return { name: RouteName.M3CreditCheckPermissionView, params: { countryCode: getCountryCode() } };
  }
  return handlePreviousImageUploadPage();
};

const offersPreviousStep = () => {
  const vuexStore = store as unknown as Store<IVuexState>;
  const isQuickApprovalFlow =
    vuexStore.getters["merchant/usesQuickApprovalFlow"];

  if (isQuickApprovalFlow) {
    return { name: RouteName.M3CustomerRegistration, params: { countryCode: getCountryCode() } };
  }

  return { name: RouteName.M3CustomerContactInfoView, params: { countryCode: getCountryCode() } };
};

const contractPreviousStep = () => {
  const merchantCountry = vuexStore?.state.merchant?.merchant?.country;
  const newOffersUI =
    vuexStore?.state.merchant?.merchantFlags?.includes(
      MerchantFlags.NEW_OFFERS_UI
    ) ?? false;

  if (merchantCountry === MerchantCountry.BR) {
    return { name: RouteName.M3Imei, params: { countryCode: getCountryCode() } };
  }

  const previousStep = newOffersUI
    ? RouteName.M3PreparingOffersV2
    : RouteName.M3Offers;
  return { name: previousStep, params: { countryCode: getCountryCode() } };
};

const softwareInstallPreviousStep = () => {
  if (isEcommerceFlow) {
    return { name: RouteName.M3SelfOnboardingCartDetails, params: { countryCode: getCountryCode() } };
  }

  return { name: RouteName.M3Contract, params: { countryCode: getCountryCode() } };
};

// TODO: Ideally this will call the backend to handle prev step. For now we still handle it in the frontend
export const getPreviousStep = (
  currentRoute: RouteRecordName
): RouteLocationRaw | null => {
  switch (currentRoute) {
    case RouteName.M3IdentificationUploader:
      return { name: RouteName.M3TermsAndConditions, params: { countryCode: getCountryCode() } };
    case RouteName.M3TermsAndConditions:
      return { name: RouteName.M3Welcome, params: { countryCode: getCountryCode() } };
    case RouteName.M3ConfigurableCreditCheck:
    case RouteName.M3CreditCheckPermissionView:
      return handlePreviousImageUploadPage();
    case RouteName.M3ConfigurableCustomerContactInfo:
    case RouteName.M3CustomerContactInfoView:
      return customerContactInfoPreviousStep();
    case RouteName.M3Offers:
      return offersPreviousStep();
    case RouteName.M3Imei:
    case RouteName.M3EcommerceSummary:
      return { name: RouteName.M3Offers, params: { countryCode: getCountryCode() } };
    case RouteName.M3Contract:
      return contractPreviousStep();
    case RouteName.M3EcommerceSelectDownpaymentType:
      return { name: RouteName.M3EcommerceSummary, params: { countryCode: getCountryCode() } };
    case RouteName.M3Software:
      return softwareInstallPreviousStep();
    case RouteName.M3ConfigurablePolicy:
      return { name: RouteName.M3Welcome, params: { countryCode: getCountryCode() } };
    default:
      return null;
  }
};

const isCheckoutOnPersonManagement = computed(() => {
  return store.getters["carts/hasActiveFeature"](
    CartActiveFeatures.CHECKOUT_ON_PERSON_MANAGEMENT
  );
});

const handlePreviousImageUploadPage = () => {
  if (isCheckoutOnPersonManagement.value) {
    return {
      name: PersonRegistrationRouteName.PersonRegistration,
      params: {
        registrationTag: vuexStore?.state.carts?.lastPersonRegistrationTag,
      },
      query: {
        suggestedActionablePage:
          PersonRegistrationRouteName.IdentificationUploader,
        source: "checkout",
      },
    };
  } else {
    return { name: RouteName.M3IdentificationUploader, params: { countryCode: getCountryCode() } };
  }
};
